// react
import { useEffect, useRef } from 'react';

// shared components
import { darkTheme, lightTheme } from '@/components/highcharts/themes';

// components
import { formatNumber } from '@/utils/formatters';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

/**
 * SeriesChart - Reusable multi-series chart component
 *
 * Props:
 * - series: Array of series configurations
 *   [{ name, data: [[timestamp, value],...], type: 'area'|'line'|'column', color, yAxis: 0|1, unit }]
 * - yAxes: Array of Y-axis configurations
 *   [{ title, opposite: false }, { title, opposite: true }]
 * - height: Chart height in pixels (default: 400)
 * - isLoading: Show loading indicator
 * - title: Chart title (optional)
 */
const SeriesChart = ({
  series = [],
  yAxes = [{ title: '', opposite: false }],
  height = 400,
  isLoading = false,
  title = '',
}) => {
  const chartRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const themeOptions =
      theme.palette?.mode === 'dark' ? darkTheme : lightTheme;

    chart.update(
      {
        chart: { backgroundColor: themeOptions.chart.backgroundColor },
        title: { style: { color: themeOptions.title.style.color } },
        xAxis: {
          lineColor: themeOptions.xAxis.lineColor,
          labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          title: {
            style: themeOptions.xAxis.title?.style || {
              color: themeOptions.xAxis.title?.style?.color,
            },
          },
        },
        yAxis: yAxes.map(() => ({
          labels: { style: { color: themeOptions.yAxis.labels.style.color } },
          title: {
            style: themeOptions.yAxis.title?.style || {
              color: themeOptions.yAxis.title?.style?.color,
            },
          },
        })),
        legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
          style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
        },
      },
      false,
    );
    chart.redraw();
  }, [theme, yAxes]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) chart.showLoading('Loading data...');
    else chart.hideLoading();
  }, [isLoading]);

  const options = {
    time: { timezone: 'Europe/Copenhagen' },
    chart: {
      type: 'line',
    },
    title: { text: title || '' },
    xAxis: {
      type: 'datetime',
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
    },
    yAxis: yAxes.map((axis) => ({
      min: 0,
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
      title: { text: axis.title || '' },
      opposite: axis.opposite || false,
    })),
    plotOptions: {
      area: {
        fillOpacity: 0.3,
        marker: {
          enabled: false,
        },
      },
      line: {
        marker: {
          enabled: false,
        },
      },
      column: {
        borderColor: 'transparent',
      },
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      pointFormatter: function () {
        const seriesConfig = series.find((s) => s.name === this.series.name);
        const suffix = seriesConfig?.unit ? ` ${seriesConfig.unit}` : '';
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formatNumber(this.y, 'da-DK')}${suffix}</b><br/>`;
      },
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      xDateFormat: '%d-%m-%Y %H:%M',
    },
    legend: {
      enabled: series.length > 1,
    },
    series: series.map((s) => ({
      name: s.name,
      data: s.data || [],
      type: s.type || 'line',
      color: s.color,
      yAxis: s.yAxis || 0,
    })),
  };

  return (
    <div style={{ width: '100%' }}>
      <HighchartsReact
        highcharts={Highcharts}
        containerProps={{ style: { height } }}
        options={options}
        ref={chartRef}
      />
    </div>
  );
};

export default SeriesChart;
